/**
 * Sign-in.
 *
 * In the legacy page this is `#error-screen`, and it is BOTH the sign-in screen
 * and the one an expired link lands on - which is why the smoke suite's parity
 * assertion checks for `error-screen`. The name is kept for that reason; the
 * `data-screen` value has to match across both surfaces or one assertion cannot
 * read both.
 *
 * Two rules carried over from the original:
 *
 *   - Provider buttons stay hidden until GET /api/auth/providers confirms the
 *     server is configured. A button that 404s is worse than no button.
 *   - With no provider configured, the e-mail form IS the sign-in, so it opens
 *     without a second click to make.
 *
 * Since spec 2026-09-22 there are three ways in, not two: address + password
 * sits beside Google and Apple. The e-mail block holds ONE loud button - Sign
 * in - and the brand magic link stays under it as the quiet way, because every
 * existing customer is still on one and the transition would break without it.
 *
 * Neither the sign-in nor the link ever says whether an address is known: the
 * server answers one generic 401 for a wrong address and a wrong password
 * alike, and this screen repeats exactly that one sentence. A failure never
 * clears what was typed.
 *
 * @module web/screens/SignInScreen
 */
import { useEffect, useState } from "react";
import { useBrand } from "../state/BrandContext.tsx";
import { AppleMark, GoogleMark, PersonEmblem } from "./icons.tsx";

interface Providers {
  google: boolean;
  apple: boolean;
}

/** Copy for the two states this screen serves. */
export const SIGN_IN_PROMPT =
  "Sign in to see your briefs, your editors and what everything costs.";

/** One sentence for a wrong address and a wrong password alike. */
export const WRONG_CREDENTIALS = "That email and password don't match an account.";

type LinkState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; email: string }
  | { kind: "failed"; message: string };

type SignInState =
  | { kind: "idle" }
  | { kind: "signing" }
  | { kind: "failed"; message: string };

export function SignInScreen({ message = SIGN_IN_PROMPT }: { message?: string }) {
  const { refresh } = useBrand();
  const [providers, setProviders] = useState<Providers>({ google: false, apple: false });
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [link, setLink] = useState<LinkState>({ kind: "idle" });
  const [signin, setSignin] = useState<SignInState>({ kind: "idle" });

  useEffect(() => {
    let live = true;
    // Plain fetch, not the API client: this endpoint is deliberately
    // unauthenticated, and a 401 here would mean nothing.
    void fetch("/api/auth/providers")
      .then(r => (r.ok ? r.json() : { google: false, apple: false }))
      .catch(() => ({ google: false, apple: false }))
      .then((p: Providers) => {
        if (!live) return;
        setProviders(p);
        // Nothing configured, so the e-mail form is the only way in.
        if (!p.google && !p.apple) setEmailOpen(true);
      });
    return () => { live = false; };
  }, []);

  const anyProvider = providers.google || providers.apple;
  const busy = link.kind === "sending" || signin.kind === "signing";

  // Carry Alan's onboarding token through the provider round trip, so the
  // person lands on the brand he invited them to.
  const token = new URLSearchParams(window.location.search).get("token");
  const providerHref = (path: string) => (token ? `${path}?token=${encodeURIComponent(token)}` : path);

  async function signInWithPassword(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address || !password || busy) return;
    setLink({ kind: "idle" });
    setSignin({ kind: "signing" });
    try {
      const res = await fetch("/api/auth/password/signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: address, password }),
      });
      if (res.ok) {
        // The cookie is set. Re-reading the brand is what flips the shell to
        // the app, so the last thing this screen does is hand over.
        await refresh();
        return;
      }
      setSignin({ kind: "failed", message: signInFailure(res.status) });
    } catch {
      setSignin({ kind: "failed", message: "Network error. Check your connection and try again." });
    }
    // Nothing is cleared on the way out: the address stays typed, and only the
    // password is worth entering again.
  }

  async function requestLink() {
    const address = email.trim();
    if (!address || busy) return;
    setSignin({ kind: "idle" });
    setLink({ kind: "sending" });
    try {
      const res = await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: address }),
      });
      if (res.status === 429) {
        setLink({ kind: "failed", message: "Too many attempts - please wait a minute and try again." });
        return;
      }
      if (!res.ok) {
        setLink({ kind: "failed", message: "Something went wrong. Please try again or email hey@aditor.ai." });
        return;
      }
      setLink({ kind: "sent", email: address });
    } catch {
      setLink({ kind: "failed", message: "Network error. Check your connection and try again." });
    }
  }

  const hint = hintFor();

  return (
    <div className="screen-signin" data-screen="error-screen">
      <div className="auth-emblem"><PersonEmblem /></div>
      <h2 className="auth-title">Onboard</h2>
      <p className="auth-sub">{message}</p>

      <div className="auth-actions">
        <a className="auth-btn auth-btn-apple" href={providerHref("/api/auth/apple/start")} hidden={!providers.apple}>
          <AppleMark />
          Sign in with Apple
        </a>
        <a className="auth-btn auth-btn-google" href={providerHref("/api/auth/google/start")} hidden={!providers.google}>
          <GoogleMark />
          Continue with Google
        </a>

        <div className="auth-divider" hidden={!anyProvider}>or</div>

        {!emailOpen && (
          <button type="button" className="auth-btn auth-btn-email" onClick={() => setEmailOpen(true)}>
            Continue with email
          </button>
        )}
      </div>

      <div className={`signin-email-block${emailOpen ? " shown" : ""}`}>
        {link.kind !== "sent" && (
          <form className="signin-form" onSubmit={signInWithPassword}>
            <input
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              aria-label="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <input
              type="password"
              autoComplete="current-password"
              required
              placeholder="Your password"
              aria-label="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {signin.kind === "signing" ? "Signing in…" : "Sign in"}
            </button>
            {/* The quiet way, for everyone who has not set a password yet. */}
            <button type="button" className="auth-btn auth-btn-email signin-link-alt" onClick={requestLink} disabled={busy}>
              {link.kind === "sending" ? "Sending…" : "Email me a sign-in link instead"}
            </button>
          </form>
        )}
        <p className={`signin-hint${hint.err ? " err" : ""}`}>{hint.text}</p>
      </div>
    </div>
  );

  /** One line under the form: what just happened, or nothing to say yet. */
  function hintFor(): { text: React.ReactNode; err: boolean } {
    if (link.kind === "sent") {
      return {
        text: <>If an account exists for <strong>{link.email}</strong>, a sign-in link is on its way. Check your inbox.</>,
        err: false,
      };
    }
    if (signin.kind === "failed") return { text: signin.message, err: true };
    if (link.kind === "failed") return { text: link.message, err: true };
    return { text: "No password yet? Use the link, and the portal will help you set one.", err: false };
  }
}

/**
 * The sentence for a refused sign-in.
 *
 * 401 and 400 share one answer on purpose - the server's own rule is that
 * nothing here reveals whether an address exists, and a second sentence for
 * "malformed" would start doing exactly that.
 */
function signInFailure(status: number): string {
  if (status === 429) return "Too many attempts. Please wait a few minutes and try again.";
  if (status === 401 || status === 400) return WRONG_CREDENTIALS;
  return "Something went wrong. Please try again or email hey@aditor.ai.";
}
